import { isPendingPersonaSetId } from "./persona-opportunity-set-generation-state.js";

export type HubScriptAction = "create" | "import";

export interface HubScriptTargetSet {
  id: string;
  type: "PERSONA" | "COMPETITOR" | "USER_GENERATED" | "POST_FLIGHT";
}

export type HubScriptTarget =
  | { showPicker: false; setId: string }
  | { showPicker: true };

/** A set the script and import APIs can accept. Optimistic rows are not ready yet. */
export function isSelectableHubScriptSetId(id: string): boolean {
  return id.length > 0 && !isPendingPersonaSetId(id);
}

export function isHubScriptSetEligible(set: HubScriptTargetSet, _action: HubScriptAction): boolean {
  if (!isSelectableHubScriptSetId(set.id)) return false;
  return set.type === "USER_GENERATED";
}

/**
 * Scratch custom scripts can target persona, competitor, or user-generated sets
 * when that set is the only hub scope. Post-Flight uses the in-dialog picker.
 */
export function isHubScriptScratchTarget(set: HubScriptTargetSet): boolean {
  if (!isSelectableHubScriptSetId(set.id)) return false;
  return (
    set.type === "USER_GENERATED" || set.type === "PERSONA" || set.type === "COMPETITOR"
  );
}

/**
 * One selected eligible set opens the dialog on that set. Any other selection,
 * including none, keeps the in-dialog picker.
 */
export function resolveHubScriptTarget(
  sets: readonly HubScriptTargetSet[],
  selectedIds: readonly string[],
  action: HubScriptAction,
): HubScriptTarget {
  if (selectedIds.length !== 1) return { showPicker: true };
  const selected = sets.find((set) => set.id === selectedIds[0]);
  if (!selected || !isHubScriptSetEligible(selected, action)) return { showPicker: true };
  return { showPicker: false, setId: selected.id };
}
